/**
 * Date helpers for the DatePicker. Canonical value is an ISO date string
 * "YYYY-MM-DD" (locale-agnostic, API-safe); display is localized via Intl.
 */

const pad2 = (n: number) => String(n).padStart(2, "0");

export function toISODate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function parseISODate(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDisplayDate(iso: string, locale: string): string {
  const d = parseISODate(iso);
  if (!d) return "";
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(d);
}

export function monthLabel(view: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(view);
}

/** Localized short month names (Jan…Dec), index 0–11. */
export function monthNames(locale: string): string[] {
  const fmt = new Intl.DateTimeFormat(locale, { month: "short" });
  return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(2023, i, 1)));
}

// --- Date + time (canonical "YYYY-MM-DDTHH:mm", local) ----------------------
export function toISODateTime(d: Date): string {
  return `${toISODate(d)}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function parseISODateTime(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDisplayDateTime(iso: string, locale: string): string {
  const d = parseISODateTime(iso);
  if (!d) return "";
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(d);
}

// --- Typeable (editable) date input: locale-ordered numeric d/m/y -----------
export interface DateFieldFormat {
  order: ("day" | "month" | "year")[];
  separator: string;
}

/** Discover the locale's day/month/year order and separator (e.g. dd.MM.yyyy). */
export function getDateFormat(locale: string): DateFieldFormat {
  const parts = new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(2023, 0, 9));
  const order: ("day" | "month" | "year")[] = [];
  let separator = "";
  for (const p of parts) {
    if (p.type === "day" || p.type === "month" || p.type === "year") order.push(p.type);
    else if (p.type === "literal" && !separator) {
      const c = p.value.trim();
      if (c) separator = c[0]!;
    }
  }
  if (order.length !== 3) return { order: ["day", "month", "year"], separator: separator || "." };
  return { order, separator: separator || "." };
}

/** Render an ISO date as the locale's editable numeric string (e.g. "09.02.2025"). */
export function formatEditable(iso: string, locale: string): string {
  const d = parseISODate(iso);
  if (!d) return "";
  const { order, separator } = getDateFormat(locale);
  const map = { day: pad2(d.getDate()), month: pad2(d.getMonth() + 1), year: String(d.getFullYear()) };
  return order.map((o) => map[o]).join(separator);
}

/** Placeholder hint for the editable input (e.g. "dd.MM.yyyy"). */
export function editablePlaceholder(locale: string): string {
  const { order, separator } = getDateFormat(locale);
  const map = { day: "dd", month: "MM", year: "yyyy" };
  return order.map((o) => map[o]).join(separator);
}

/** Parse a typed date (any separator) using the locale's field order → ISO, or null. */
export function parseEditable(text: string, locale: string): string | null {
  const nums = text.split(/\D+/).filter(Boolean);
  if (nums.length < 3) return null;
  const { order } = getDateFormat(locale);
  let day = 0;
  let month = 0;
  let year = 0;
  order.forEach((o, i) => {
    const n = Number(nums[i] ?? "0");
    if (o === "day") day = n;
    else if (o === "month") month = n;
    else year = n;
  });
  if (year < 100) year += 2000;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null; // rejects e.g. 31.02
  return toISODate(d);
}

export function formatEditableDateTime(iso: string, locale: string): string {
  const d = parseISODateTime(iso);
  if (!d) return "";
  return `${formatEditable(toISODate(d), locale)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** Parse a typed date+time → ISO "YYYY-MM-DDThh:mm", or null. */
export function parseEditableDateTime(text: string, locale: string): string | null {
  const nums = text.split(/\D+/).filter(Boolean);
  if (nums.length < 3) return null;
  const datePart = parseEditable(nums.slice(0, 3).join("."), locale);
  if (!datePart) return null;
  const hh = nums.length > 3 ? Number(nums[3]) : 0;
  const mm = nums.length > 4 ? Number(nums[4]) : 0;
  if (hh > 23 || mm > 59) return null;
  return `${datePart}T${pad2(hh)}:${pad2(mm)}`;
}

export function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Localized short weekday labels, ordered from `firstDay` (0=Sun, 1=Mon). */
export function weekdayLabels(locale: string, firstDay = 1): string[] {
  const fmt = new Intl.DateTimeFormat(locale, { weekday: "short" });
  const out: string[] = [];
  for (let i = 0; i < 7; i++) {
    const day = (firstDay + i) % 7;
    out.push(fmt.format(new Date(2023, 0, 1 + day))); // 2023-01-01 is a Sunday
  }
  return out;
}

/** 6×7 day grid (42 cells) covering the view month, starting on `firstDay`. */
export function monthMatrix(view: Date, firstDay = 1): Date[] {
  const first = new Date(view.getFullYear(), view.getMonth(), 1);
  let lead = first.getDay() - firstDay;
  if (lead < 0) lead += 7;
  const start = new Date(first.getFullYear(), first.getMonth(), 1 - lead);
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    days.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }
  return days;
}
